import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Trophy, 
  Camera, 
  DollarSign, 
  Calendar, 
  Users, 
  ChevronRight,
  Check,
  Zap,
  Target,
  TrendingUp,
  Globe,
  Clock,
  ArrowRight,
  Play,
  Star,
  Shield,
  Smartphone,
  User,
  Mail,
  Phone,
  Briefcase,
  ChevronLeft,
  Loader2,
  CheckCircle2,
  CircleDot
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_LABELS = ['Basic Info', 'Organizer Profile', 'Goals & Intent', 'Final'];

const ORGANIZER_TYPES = [
  'Golf League Organizer',
  'Tournament Host',
  'Influencer',
  'Course Staff',
  'Other'
];

const NETWORK_SIZES = ['0–20', '20–50', '50–100', '100+'];

const EVENT_FREQUENCIES = [
  'Monthly',
  'Quarterly',
  'Occasionally',
  'Not yet but planning to'
];

const INTEREST_OPTIONS = [
  'Running tournaments easier',
  'Making money from events',
  'Growing my golf network',
  'Sponsorship opportunities',
  'Fundraising events'
];

export default function AdminRecruitLanding() {
  const [isVisible, setIsVisible] = useState({});
  const [formStep, setFormStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    organizer_type: '',
    network_size: '',
    event_frequency: '',
    interests: [],
    events_per_year: '',
    additional_info: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToApply = () => {
    document.getElementById('apply-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError('');
  };

  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const validateStep = () => {
    if (formStep === 0) {
      if (!formData.full_name.trim()) return 'Full name is required';
      if (!formData.email.trim()) return 'Email is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    }
    return '';
  };

  const nextStep = () => {
    const err = validateStep();
    if (err) { setFormError(err); return; }
    setFormError('');
    setFormStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setFormError('');
    setFormStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setFormError('');
    try {
      const payload = JSON.stringify({
        ...formData,
        events_per_year: parseInt(formData.events_per_year) || 0
      });
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/api/applications/admin`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = () => {
          let data = {};
          try { data = JSON.parse(xhr.responseText); } catch {}
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else if (xhr.status === 409) {
            reject(new Error('An application with this email already exists'));
          } else {
            reject(new Error(data.detail || 'Submission failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error. Please try again.'));
        xhr.send(payload);
      });
      setSubmitted(true);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f0d] text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0f0d]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="https://thebundlegolfclubusa.com" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="nav-main-site-link">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg tracking-tight">THE BUNDLE</span>
                <span className="text-emerald-400 text-xs block -mt-1">GOLF CLUB</span>
              </div>
            </a>
            <Button 
              onClick={scrollToApply}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-6 hidden sm:flex"
            >
              Apply Now
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16">
        {/* Video Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          >
            <source src="/videos/admin-promo.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a0f0d] via-[#0a0f0d]/80 to-[#0a0f0d]/90" />
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-[#0a0f0d] to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl">
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8"
              data-animate
              id="hero-badge"
            >
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-sm text-emerald-400 font-medium">Now Recruiting Golf Club Directors</span>
            </div>

            {/* Headline */}
            <h1 
              className={`text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.9] mb-6 transition-all duration-1000 ${isVisible['hero-badge'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              The Future of
              <br />
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-teal-400 bg-clip-text text-transparent">
                Golf Groups
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl sm:text-2xl text-gray-400 max-w-2xl mb-10 leading-relaxed">
              Run your tournaments. Grow your network. 
              <span className="text-white font-medium"> Increase your opportunities.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={scrollToApply}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg px-8 py-6 rounded-xl group"
              >
                Apply to Become a Golf Club Director
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                variant="outline"
                onClick={scrollToApply}
                className="border-white/20 text-white hover:bg-white/10 font-semibold text-lg px-8 py-6 rounded-xl"
              >
                <Play className="w-5 h-5 mr-2" />
                See How It Works
              </Button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 mt-16 pt-8 border-t border-white/10">
              {[
                { value: '500+', label: 'Active Golfers' },
                { value: '50+', label: 'Tournaments Hosted' },
                { value: '$25K+', label: 'Payouts Processed' }
              ].map((stat, i) => (
                <div key={i} className="text-center sm:text-left">
                  <div className="text-3xl sm:text-4xl font-black text-emerald-400">{stat.value}</div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating UI Preview */}
        <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-[500px] opacity-60">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0a0f0d] z-10" />
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-6 backdrop-blur border border-white/10 transform rotate-3 hover:rotate-0 transition-transform duration-500">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-bold">Live Leaderboard</span>
                <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">LIVE</span>
              </div>
              {['Calvin P.', 'Andre S.', 'Reggie K.'].map((name, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                    <span>{name}</span>
                  </div>
                  <span className="text-emerald-400 font-bold">{72 + i}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Why Directors Are Joining */}
      <section className="py-24 relative" id="why-join" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-400 text-sm font-bold uppercase tracking-widest">Why Directors Are Joining</span>
            <h2 className="text-4xl sm:text-5xl font-black mt-4 mb-6">
              More Than Just an App
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              A complete system designed to simplify running golf events and help you grow, organize, and elevate your group.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Zap className="w-8 h-8" />,
                title: 'Effortless Organization',
                description: 'Stop juggling spreadsheets, texts, and Venmo. Everything runs from one platform.',
                color: 'from-yellow-500 to-orange-500'
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: 'Accelerated Growth',
                description: 'Tap into a network of golfers actively looking for games and tournaments.',
                color: 'from-emerald-500 to-teal-500'
              },
              {
                icon: <Star className="w-8 h-8" />,
                title: 'Premium Experience',
                description: 'Give your players a professional-grade experience that keeps them coming back.',
                color: 'from-purple-500 to-pink-500'
              }
            ].map((item, i) => (
              <Card 
                key={i} 
                className={`bg-gradient-to-br from-gray-800/30 to-gray-900/30 border-white/5 overflow-hidden group hover:border-emerald-500/30 transition-all duration-500 ${isVisible['why-join'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <CardContent className="p-8">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    {item.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: Run Tournaments Like a Pro */}
      <section className="py-24 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent" id="features" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-400 text-sm font-bold uppercase tracking-widest">Platform Features</span>
            <h2 className="text-4xl sm:text-5xl font-black mt-4 mb-6">
              Run Tournaments <span className="text-emerald-400">Like a Pro</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Trophy className="w-6 h-6" />,
                title: 'Live Leaderboard',
                benefit: 'Real-time scoring that keeps players engaged',
                bullets: ['Instant score updates', 'No more confusion', 'Automatic rankings'],
                gradient: 'from-emerald-500/20 to-teal-500/20',
                borderColor: 'border-emerald-500/30'
              },
              {
                icon: <Camera className="w-6 h-6" />,
                title: 'Scan Scorecard',
                benefit: 'Take a picture to input scores instantly',
                bullets: ['AI-powered OCR', 'No manual entry', 'Faster results'],
                gradient: 'from-blue-500/20 to-cyan-500/20',
                borderColor: 'border-blue-500/30'
              },
              {
                icon: <DollarSign className="w-6 h-6" />,
                title: 'Automatic Payouts',
                benefit: 'Handles skins and winnings effortlessly',
                bullets: ['Eliminates disputes', 'No calculations', 'Transparent splits'],
                gradient: 'from-yellow-500/20 to-orange-500/20',
                borderColor: 'border-yellow-500/30'
              },
              {
                icon: <Calendar className="w-6 h-6" />,
                title: 'Tournament Calendar',
                benefit: 'Organize events in one centralized place',
                bullets: ['Automatic notifications', 'Tee time management', 'Calendar sync'],
                gradient: 'from-purple-500/20 to-pink-500/20',
                borderColor: 'border-purple-500/30'
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Built-In Network',
                benefit: 'Reach more golfers instantly',
                bullets: ['Fill events faster', 'Expand your group', 'Cross-promote games'],
                gradient: 'from-rose-500/20 to-red-500/20',
                borderColor: 'border-rose-500/30'
              },
              {
                icon: <Smartphone className="w-6 h-6" />,
                title: 'Mobile-First App',
                benefit: 'Everything works perfectly on phones',
                bullets: ['No app downloads', 'PWA technology', 'Works offline'],
                gradient: 'from-indigo-500/20 to-violet-500/20',
                borderColor: 'border-indigo-500/30'
              }
            ].map((feature, i) => (
              <div 
                key={i}
                className={`bg-gradient-to-br ${feature.gradient} rounded-2xl p-6 border ${feature.borderColor} hover:scale-[1.02] transition-all duration-300 ${isVisible['features'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-white">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white">{feature.title}</h3>
                </div>
                <p className="text-gray-200 font-medium mb-4">{feature.benefit}</p>
                <ul className="space-y-2">
                  {feature.bullets.map((bullet, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Community + Network */}
      <section className="py-24 relative overflow-hidden" id="community" data-animate>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-400 text-sm font-bold uppercase tracking-widest">The Bundle Golf Club</span>
              <h2 className="text-4xl sm:text-5xl font-black mt-4 mb-6">
                Join a Network of <span className="text-emerald-400">Golf Leaders</span>
              </h2>
              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Connect with other golf organizers, share insights, and grow together. 
                This isn't just a platform — it's a movement.
              </p>
              
              <div className="space-y-4">
                {[
                  'Access a network of proven golf organizers',
                  'Learn systems and formats that work',
                  'Collaborate on larger events',
                  'Grow beyond your local group'
                ].map((item, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center gap-4 ${isVisible['community'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${i * 100}ms`, transition: 'all 0.6s ease-out' }}
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-emerald-400" />
                    </div>
                    <span className="text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Network Visualization */}
            <div className="relative h-[400px] hidden lg:block">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-full h-full">
                  {/* Center Node */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/30 z-10">
                    <Trophy className="w-10 h-10 text-white" />
                  </div>
                  
                  {/* Orbiting Nodes */}
                  {[0, 1, 2, 3, 4, 5].map((i) => {
                    const angle = (i * 60) * (Math.PI / 180);
                    const radius = 140;
                    const x = Math.cos(angle) * radius;
                    const y = Math.sin(angle) * radius;
                    return (
                      <div
                        key={i}
                        className="absolute top-1/2 left-1/2 w-14 h-14 bg-gray-800 rounded-full border-2 border-emerald-500/30 flex items-center justify-center"
                        style={{ 
                          transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                          animation: `pulse 2s ease-in-out ${i * 0.2}s infinite`
                        }}
                      >
                        <Users className="w-6 h-6 text-emerald-400" />
                      </div>
                    );
                  })}
                  
                  {/* Connecting Lines */}
                  <svg className="absolute inset-0 w-full h-full" style={{ transform: 'translate(-0%, -0%)' }}>
                    {[0, 1, 2, 3, 4, 5].map((i) => {
                      const angle = (i * 60) * (Math.PI / 180);
                      const x = 200 + Math.cos(angle) * 140;
                      const y = 200 + Math.sin(angle) * 140;
                      return (
                        <line
                          key={i}
                          x1="200"
                          y1="200"
                          x2={x}
                          y2={y}
                          stroke="rgba(16, 185, 129, 0.2)"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                        />
                      );
                    })}
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Grow Your Golf Group */}
      <section className="py-24" id="grow" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 rounded-3xl p-8 sm:p-12 lg:p-16 border border-emerald-500/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-emerald-500/5 to-transparent" />
            
            <div className="relative z-10 max-w-3xl">
              <h2 className="text-4xl sm:text-5xl font-black mb-6">
                Bigger Network. <span className="text-emerald-400">Bigger Events.</span>
              </h2>
              <p className="text-xl text-gray-300 mb-10 leading-relaxed">
                Expand beyond your immediate circle. Reach more players, host larger outings, 
                and create a stronger, more consistent golf community.
              </p>
              
              <div className="grid sm:grid-cols-3 gap-8">
                {[
                  { icon: <Globe className="w-8 h-8" />, label: 'Wider Reach', sublabel: 'More golfers' },
                  { icon: <Target className="w-8 h-8" />, label: 'Larger Events', sublabel: 'More energy' },
                  { icon: <Users className="w-8 h-8" />, label: 'Stronger Community', sublabel: 'More loyalty' }
                ].map((item, i) => (
                  <div key={i} className="text-center sm:text-left">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-400 mx-auto sm:mx-0 mb-4">
                      {item.icon}
                    </div>
                    <div className="font-bold text-lg">{item.label}</div>
                    <div className="text-emerald-400 text-sm">{item.sublabel}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: What This Means For You */}
      <section className="py-24" id="benefits" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-400 text-sm font-bold uppercase tracking-widest">The Bottom Line</span>
            <h2 className="text-4xl sm:text-5xl font-black mt-4">
              What This Means <span className="text-emerald-400">For You</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              'Run tournaments without spreadsheets',
              'Attract more players effortlessly',
              'Create better experiences every round',
              'Build a real golf community',
              'Elevate your role as an organizer',
              'Turn your passion into opportunity'
            ].map((item, i) => (
              <div 
                key={i}
                className={`flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-emerald-500/10 transition-colors ${isVisible['benefits'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${i * 50}ms`, transition: 'all 0.5s ease-out' }}
              >
                <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-black" />
                </div>
                <span className="font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Social Proof / Vision */}
      <section className="py-24 relative" id="vision" data-animate>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/10 via-transparent to-emerald-900/10" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8">
            <Star className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-400 font-medium">The Vision</span>
          </div>
          
          <div className={`text-2xl sm:text-3xl lg:text-4xl leading-relaxed ${isVisible['vision'] ? 'opacity-100' : 'opacity-0'}`} style={{ transition: 'opacity 1s ease-out' }}>
            <p className="text-gray-400 mb-6">
              "You've got <span className="text-white font-bold">20+ golfers</span> showing up every week.
            </p>
            <p className="text-gray-400 mb-6">
              There's <span className="text-emerald-400 font-bold">competition</span>, <span className="text-emerald-400 font-bold">energy</span>, and <span className="text-emerald-400 font-bold">consistency</span>.
            </p>
            <p className="text-white font-bold">
              Your group becomes the game everyone wants to be part of."
            </p>
          </div>
        </div>
      </section>

      {/* Section 7: Speed + Ease */}
      <section className="py-24" id="speed" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <Clock className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-400 font-medium">Quick Start</span>
              </div>
              
              <h2 className="text-4xl sm:text-5xl font-black mb-6">
                Launch Your First Game in <span className="text-emerald-400">7 Days</span>
              </h2>
              
              <p className="text-xl text-gray-400 mb-8">
                No tech skills required. Simple setup. We help you get started quickly.
              </p>
              
              <div className="space-y-4">
                {[
                  { step: '1', title: 'Apply & Get Approved', desc: 'Quick application process' },
                  { step: '2', title: 'Set Up Your Group', desc: 'We guide you through setup' },
                  { step: '3', title: 'Invite Your Players', desc: 'Easy sharing tools' },
                  { step: '4', title: 'Host Your First Event', desc: 'Full support included' }
                ].map((item, i) => (
                  <div 
                    key={i}
                    className={`flex items-center gap-4 p-4 rounded-xl bg-white/5 ${isVisible['speed'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                    style={{ transitionDelay: `${i * 100}ms`, transition: 'all 0.5s ease-out' }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center font-black text-black">
                      {item.step}
                    </div>
                    <div>
                      <div className="font-bold">{item.title}</div>
                      <div className="text-sm text-gray-400">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Phone Mockup */}
            <div className="relative flex justify-center">
              <div className="relative w-[280px] h-[580px] bg-gray-900 rounded-[40px] p-3 border-4 border-gray-800 shadow-2xl">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-2xl" />
                <div className="w-full h-full bg-gradient-to-br from-emerald-900/50 to-gray-900 rounded-[32px] overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-6 mt-4">
                      <div className="text-lg font-bold">The Bundle</div>
                      <div className="w-8 h-8 bg-emerald-500 rounded-full" />
                    </div>
                    <div className="space-y-3">
                      <div className="bg-white/10 rounded-xl p-4">
                        <div className="text-xs text-emerald-400 mb-1">NEXT EVENT</div>
                        <div className="font-bold">Saturday Skins Game</div>
                        <div className="text-sm text-gray-400">12 players registered</div>
                      </div>
                      <div className="bg-white/10 rounded-xl p-4">
                        <div className="text-xs text-emerald-400 mb-1">LEADERBOARD</div>
                        <div className="flex justify-between items-center">
                          <span>Brandon T.</span>
                          <span className="text-emerald-400 font-bold">-3</span>
                        </div>
                      </div>
                      <div className="bg-emerald-500 rounded-xl p-4 text-center">
                        <div className="font-bold text-black">Post Your Score</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 8: Application Form */}
      <section className="py-24 relative" id="apply-section">
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            
            {/* Left: Benefits */}
            <div className="lg:col-span-2 hidden lg:block">
              <h2 className="text-3xl font-black mb-6">
                Why Become a
                <span className="text-emerald-400"> Director?</span>
              </h2>
              <div className="space-y-4">
                {[
                  'Run and manage tournaments with ease',
                  'Monetize your golf network',
                  'Access to players and sponsors',
                  'Built-in scoring, payments & event tools',
                  'Be part of a growing golf ecosystem'
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-sm text-gray-400 italic">
                  "I went from running pickup games to hosting 50+ player tournaments with live leaderboards. This platform changed everything."
                </p>
                <p className="text-sm text-emerald-400 mt-2 font-medium">— Jamal R., Bundle Golf Club Director</p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#111916] border border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/5">
                
                {submitted ? (
                  /* Success State */
                  <div className="p-8 sm:p-12 text-center" data-testid="application-success">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                    </div>
                    <h3 className="text-2xl font-black mb-3">You're In.</h3>
                    <p className="text-gray-400 max-w-sm mx-auto">
                      Our team will review your application and contact you within <span className="text-white font-medium">24–48 hours</span>.
                    </p>
                    <div className="mt-8 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                      <p className="text-sm text-emerald-300">While you wait, check out what directors are already doing:</p>
                      <a href="https://thebundlegolfclubusa.com" className="text-emerald-400 font-semibold text-sm hover:underline mt-1 inline-block">Visit The Bundle Golf Club →</a>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Form Header */}
                    <div className="px-6 sm:px-8 pt-8 pb-4">
                      <h3 className="text-2xl font-black mb-1" data-testid="form-title">Apply to Become a Director</h3>
                      <p className="text-gray-400 text-sm">Step {formStep + 1} of {STEP_LABELS.length} — {STEP_LABELS[formStep]}</p>
                      
                      {/* Progress Bar */}
                      <div className="flex gap-2 mt-4" data-testid="form-progress">
                        {STEP_LABELS.map((_, i) => (
                          <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/10">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${
                                i < formStep ? 'bg-emerald-400 w-full' : 
                                i === formStep ? 'bg-emerald-400 w-1/2' : 'w-0'
                              }`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Form Body */}
                    <div className="px-6 sm:px-8 py-6 min-h-[320px]">
                      {formError && (
                        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="form-error">
                          {formError}
                        </div>
                      )}

                      {/* Step 1: Basic Info */}
                      {formStep === 0 && (
                        <div className="space-y-5" data-testid="form-step-1">
                          <div>
                            <label className="text-sm text-gray-400 mb-1.5 block">Full Name <span className="text-emerald-400">*</span></label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                              <input
                                data-testid="input-full-name"
                                type="text"
                                value={formData.full_name}
                                onChange={e => updateField('full_name', e.target.value)}
                                placeholder="Your full name"
                                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-400 mb-1.5 block">Email <span className="text-emerald-400">*</span></label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                              <input
                                data-testid="input-email"
                                type="email"
                                value={formData.email}
                                onChange={e => updateField('email', e.target.value)}
                                placeholder="you@email.com"
                                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-400 mb-1.5 block">Phone Number</label>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                              <input
                                data-testid="input-phone"
                                type="tel"
                                value={formData.phone}
                                onChange={e => updateField('phone', e.target.value)}
                                placeholder="(555) 123-4567"
                                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 2: Organizer Profile */}
                      {formStep === 1 && (
                        <div className="space-y-6" data-testid="form-step-2">
                          <div>
                            <label className="text-sm text-gray-400 mb-3 block">What best describes you?</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {ORGANIZER_TYPES.map(type => (
                                <button
                                  key={type}
                                  data-testid={`org-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                                  onClick={() => updateField('organizer_type', type)}
                                  className={`px-4 py-3 rounded-xl border text-left text-sm transition-all ${
                                    formData.organizer_type === type
                                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                                      : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/20'
                                  }`}
                                >
                                  {formData.organizer_type === type && <Check className="w-3.5 h-3.5 inline mr-2" />}
                                  {type}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-400 mb-3 block">How many golfers are in your network?</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {NETWORK_SIZES.map(size => (
                                <button
                                  key={size}
                                  data-testid={`network-size-${size}`}
                                  onClick={() => updateField('network_size', size)}
                                  className={`px-4 py-3 rounded-xl border text-center text-sm transition-all ${
                                    formData.network_size === size
                                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                                      : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/20'
                                  }`}
                                >
                                  {size}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-400 mb-3 block">How often do you host events?</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {EVENT_FREQUENCIES.map(freq => (
                                <button
                                  key={freq}
                                  data-testid={`event-freq-${freq.toLowerCase().replace(/\s+/g, '-')}`}
                                  onClick={() => updateField('event_frequency', freq)}
                                  className={`px-4 py-3 rounded-xl border text-left text-sm transition-all ${
                                    formData.event_frequency === freq
                                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                                      : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/20'
                                  }`}
                                >
                                  {formData.event_frequency === freq && <Check className="w-3.5 h-3.5 inline mr-2" />}
                                  {freq}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 3: Goals & Intent */}
                      {formStep === 2 && (
                        <div className="space-y-6" data-testid="form-step-3">
                          <div>
                            <label className="text-sm text-gray-400 mb-3 block">What are you most interested in? <span className="text-gray-600">(select all that apply)</span></label>
                            <div className="space-y-2">
                              {INTEREST_OPTIONS.map(interest => (
                                <button
                                  key={interest}
                                  data-testid={`interest-${interest.toLowerCase().replace(/\s+/g, '-').substring(0, 20)}`}
                                  onClick={() => toggleInterest(interest)}
                                  className={`w-full px-4 py-3 rounded-xl border text-left text-sm flex items-center gap-3 transition-all ${
                                    formData.interests.includes(interest)
                                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                                      : 'border-white/10 bg-white/5 text-gray-300 hover:border-white/20'
                                  }`}
                                >
                                  <div className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center ${
                                    formData.interests.includes(interest)
                                      ? 'bg-emerald-500 border-emerald-500'
                                      : 'border-white/20'
                                  }`}>
                                    {formData.interests.includes(interest) && <Check className="w-3 h-3 text-black" />}
                                  </div>
                                  {interest}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="text-sm text-gray-400 mb-1.5 block">Estimated events per year</label>
                            <input
                              data-testid="input-events-per-year"
                              type="number"
                              min="0"
                              value={formData.events_per_year}
                              onChange={e => updateField('events_per_year', e.target.value)}
                              placeholder="e.g. 12"
                              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all"
                            />
                          </div>
                        </div>
                      )}

                      {/* Step 4: Final */}
                      {formStep === 3 && (
                        <div className="space-y-5" data-testid="form-step-4">
                          <div>
                            <label className="text-sm text-gray-400 mb-1.5 block">Anything else we should know? <span className="text-gray-600">(optional)</span></label>
                            <textarea
                              data-testid="input-additional-info"
                              value={formData.additional_info}
                              onChange={e => updateField('additional_info', e.target.value)}
                              placeholder="Tell us about your golf group, events you've hosted, or anything else..."
                              rows={5}
                              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all resize-none"
                            />
                          </div>
                          
                          {/* Summary */}
                          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                            <h4 className="text-sm font-semibold text-emerald-400 mb-3">Application Summary</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Name</span>
                                <span className="text-white">{formData.full_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Email</span>
                                <span className="text-white">{formData.email}</span>
                              </div>
                              {formData.organizer_type && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Type</span>
                                  <span className="text-white">{formData.organizer_type}</span>
                                </div>
                              )}
                              {formData.network_size && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Network</span>
                                  <span className="text-white">{formData.network_size} golfers</span>
                                </div>
                              )}
                              {formData.interests.length > 0 && (
                                <div className="flex justify-between items-start">
                                  <span className="text-gray-500">Interests</span>
                                  <span className="text-white text-right max-w-[200px]">{formData.interests.length} selected</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Form Footer */}
                    <div className="px-6 sm:px-8 pb-8 flex items-center justify-between gap-3">
                      {formStep > 0 ? (
                        <Button
                          data-testid="form-back-btn"
                          variant="ghost"
                          onClick={prevStep}
                          className="text-gray-400 hover:text-white hover:bg-white/5"
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" />
                          Back
                        </Button>
                      ) : <div />}

                      {formStep < 3 ? (
                        <Button
                          data-testid="form-next-btn"
                          onClick={nextStep}
                          className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8 py-3 rounded-xl"
                        >
                          Continue
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      ) : (
                        <Button
                          data-testid="form-submit-btn"
                          onClick={handleSubmit}
                          disabled={submitting}
                          className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8 py-3 rounded-xl disabled:opacity-50"
                        >
                          {submitting ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                          ) : (
                            <>Submit Application<ArrowRight className="w-4 h-4 ml-2" /></>
                          )}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <p className="text-center text-xs text-gray-600 mt-4">
                <span className="text-emerald-400/60 font-semibold">Limited director spots per city</span> — Apply now to secure your position
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <a href="https://thebundlegolfclubusa.com" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="footer-main-site-link">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold">The Bundle Golf Club</span>
            </a>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <a href="https://thebundlegolfclubusa.com" className="hover:text-emerald-400 transition-colors" data-testid="footer-visit-site-link">Visit Main Site</a>
              <a href="/fundraiser" className="hover:text-emerald-400 transition-colors">Charity Events</a>
            </div>
          </div>
          <div className="text-center text-sm text-gray-500 mt-8">
            © 2026 The Bundle Golf Club. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: translate(calc(-50% + var(--x)), calc(-50% + var(--y))) scale(1); opacity: 1; }
          50% { transform: translate(calc(-50% + var(--x)), calc(-50% + var(--y))) scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}
